// navRegistry — single source of truth for all navigation groups and items.
// Consumed by Sidebar, MobileNav, and buildGroupRoutes.
// Rule §14.1: no routes in App.tsx, no hand-coded sidebar items.

import { createElement, lazy } from 'react'
import type { ComponentType } from 'react'
import {
  Gift,
  Activity,
  BarChart3,
  Tag,
  Users,
  UserCog,
  Settings,
  Compass,
  IdCard,
  BookOpen,
  CalendarDays,
  Clock,
  Store,
  Sparkles,
  CalendarCheck,
  ShoppingBag,
  ClipboardList,
  SlidersHorizontal,
  CreditCard,
  Boxes,
} from 'lucide-react'
import type { NavGroup } from '@synvya/portal-ui/types'
import { UpgradeGate } from '@/components/UpgradeGate'
import type { GateFeature } from '@/components/UpgradeGate'

/**
 * Premium pages stay in the nav (the upsell is the point) and gate at the
 * page body: a Basic restaurant sees the upgrade card where the editor would
 * be (billing-signup.md §5.5). Declared here, beside the item, so the
 * registry stays the one list of what is Premium. `roleGate` is not
 * overloaded for this — a role is who you are, a plan is what you pay for.
 */
export function premium(feature: GateFeature, Page: ComponentType): ComponentType {
  function PremiumGated() {
    return createElement(UpgradeGate, { feature, children: createElement(Page) })
  }
  PremiumGated.displayName = `Premium(${feature})`
  return PremiumGated
}

export const APP_BASE = '/app'

const discoveryGroup: NavGroup = {
  id: 'discovery',
  label: 'nav.discovery',
  Icon: Compass,
  accent: 'emerald',
  basePath: '/app/loyalty',
  condensed: true,
  mobileTabHidden: true,
  items: [
    {
      to: '/app/loyalty/pulse',
      label: 'nav.pulse',
      Icon: Activity,
      element: premium('pulse', lazy(
        () => import('@/groups/loyalty/pages/PulsePage')
      )),
      mobilePromote: true,
    },
    {
      // Beside Pulse, because it answers the same kind of question about the
      // same audience: Pulse is who came, this is what they opened.
      to: '/app/loyalty/content',
      label: 'nav.contentReport',
      Icon: BarChart3,
      element: premium('pulse', lazy(
        () => import('@/groups/loyalty/pages/ContentReportPage')
      )),
    },
    {
      to: '/app/loyalty',
      label: 'nav.marketingKit',
      Icon: Gift,
      element: lazy(
        () => import('@/groups/loyalty/pages/LoyaltyHomePage')
      ),
      mobilePromote: true,
    },
    {
      to: '/app/loyalty/offers',
      label: 'nav.offers',
      Icon: Tag,
      element: premium('offers', lazy(
        () => import('@/groups/offers/pages/OfferManagementPage')
      )),
    },
    {
      to: '/app/loyalty/events',
      label: 'nav.events',
      Icon: CalendarDays,
      element: premium('events', lazy(
        () => import('@/groups/offers/pages/EventsPage')
      )),
    },
    {
      to: '/app/loyalty/happy-hour',
      label: 'nav.happyHour',
      Icon: Clock,
      element: premium('happyHour', lazy(
        () => import('@/groups/offers/pages/HappyHourPage')
      )),
    },
    {
      // Phase 4 — Specials authoring. Scaffolded (placeholder) pending the
      // kind:30405 collection builder; hidden from nav until the page is real.
      to: '/app/loyalty/specials',
      label: 'nav.specials',
      Icon: Sparkles,
      element: premium('specials', lazy(
        () => import('@/groups/offers/pages/SpecialsPage')
      )),
      hidden: true,
    },
  ],
}

const reservationsGroup: NavGroup = {
  id: 'reservations',
  label: 'nav.reservations',
  Icon: CalendarCheck,
  // PRD proposes teal; GroupAccent has no teal, so violet (unused elsewhere).
  accent: 'violet',
  basePath: '/app/reservations',
  condensed: true,
  items: [
    {
      to: '/app/reservations',
      label: 'nav.reservationsBook',
      Icon: CalendarCheck,
      element: premium('reservations', lazy(
        () => import('@/groups/reservations/pages/BookPage')
      )),
    },
    {
      to: '/app/reservations/report',
      label: 'nav.reservationsReport',
      Icon: Activity,
      element: premium('reservations', lazy(
        () => import('@/groups/reservations/pages/ReservationsReportPage')
      )),
    },
  ],
}

// Its own top-level group, beside Reservations rather than inside Discovery.
// Discovery is what a diner is offered; this is what the kitchen works, and it
// is the only screen in the portal with a person waiting at the other end of
// it. Decided 2026-08-30 — docs/specs/takeout.md §5.1.
const takeoutGroup: NavGroup = {
  id: 'takeout',
  label: 'nav.takeout',
  Icon: ShoppingBag,
  accent: 'amber',
  basePath: '/app/takeout',
  condensed: true,
  items: [
    {
      to: '/app/takeout/orders',
      label: 'nav.takeoutOrders',
      Icon: ClipboardList,
      element: premium('takeout', lazy(
        () => import('@/groups/takeout/pages/TakeoutOrdersPage')
      )),
    },
    {
      to: '/app/takeout/report',
      label: 'nav.takeoutReport',
      Icon: Activity,
      element: premium('takeout', lazy(
        () => import('@/groups/takeout/pages/TakeoutReportPage')
      )),
    },
    {
      to: '/app/takeout/horizon',
      label: 'nav.takeoutHorizon',
      Icon: ClipboardList,
      element: premium('takeout', lazy(
        () => import('@/groups/takeout/pages/TakeoutStockHorizonPage')
      )),
    },
    {
      to: '/app/takeout/stock',
      label: 'nav.takeoutStock',
      Icon: Boxes,
      element: premium('takeout', lazy(
        () => import('@/groups/takeout/pages/TakeoutStockPage')
      )),
      roleGate: 'admin',
    },
    {
      to: '/app/takeout/setup',
      label: 'nav.takeoutSetup',
      Icon: SlidersHorizontal,
      element: premium('takeout', lazy(
        () => import('@/groups/takeout/pages/TakeoutSetupPage')
      )),
      roleGate: 'admin',
    },
  ],
}

const customersGroup: NavGroup = {
  id: 'customers',
  label: 'nav.customers',
  Icon: Users,
  accent: 'emerald',
  basePath: '/app/customers',
  condensed: true,
  items: [
    {
      to: '/app/customers',
      label: 'nav.customers',
      Icon: Users,
      element: premium('customers', lazy(
        () => import('@/groups/customers/pages/CustomerListPage')
      )),
    },
    {
      to: '/app/customers/metrics',
      label: 'nav.customerMetrics',
      Icon: Activity,
      element: premium('customers', lazy(
        () => import('@/groups/customers/pages/CustomerMetricsPage')
      )),
    },
  ],
}

const myRestaurantGroup: NavGroup = {
  id: 'my-restaurant',
  label: 'nav.myRestaurant',
  Icon: Store,
  accent: 'sky',
  basePath: '/app/discovery',
  mobileTabHidden: true,
  items: [
    {
      to: '/app/discovery/profile',
      label: 'nav.profile',
      Icon: IdCard,
      element: lazy(
        () => import('@/groups/discovery/pages/ProfileEditorPage')
      ),
      roleGate: 'admin',
    },
    {
      to: '/app/discovery/menu',
      label: 'nav.menu',
      Icon: BookOpen,
      element: lazy(
        () => import('@/groups/discovery/pages/MenuEditorPage')
      ),
      roleGate: 'admin',
    },
  ],
}

const settingsGroup: NavGroup = {
  id: 'settings',
  label: 'nav.settings',
  Icon: Settings,
  accent: 'slate',
  basePath: '/app/settings',
  condensed: true,
  mobileTabHidden: true,
  items: [
    {
      to: '/app/settings/staff',
      label: 'nav.users',
      Icon: UserCog,
      element: lazy(
        () => import('@/groups/settings/pages/StaffMembersPage')
      ),
      roleGate: 'admin',
    },
    {
      to: '/app/settings/plan',
      label: 'nav.plan',
      Icon: CreditCard,
      element: lazy(
        () => import('@/groups/settings/pages/PlanBillingPage')
      ),
      roleGate: 'admin',
    },
    {
      to: '/app/settings',
      // One Settings screen: profile, appearance, restaurant, feedback, about
      // and delete. The group above is "Account", so the two labels differ.
      label: 'nav.settingsItem',
      Icon: Settings,
      element: lazy(
        () => import('@/groups/settings/pages/SettingsPage')
      ),
    },
  ],
}

// My Restaurant first (decided 2026-09-08): profile and menu are what every
// restaurant sets up first and what everything Premium builds on. The same
// order for every plan — a sidebar that rearranges itself on upgrade is a
// support conversation waiting to happen.
export const groups: readonly NavGroup[] = [
  myRestaurantGroup,
  discoveryGroup,
  reservationsGroup,
  takeoutGroup,
  customersGroup,
  settingsGroup,
] as const

/**
 * Where a sign-in lands. `/app` itself: its index route (`LandingRedirect`)
 * picks by plan — Pulse for Premium, the Profile for Basic — so a Basic
 * restaurant is not greeted by an upgrade card on every visit
 * (decided 2026-09-08). Callers that want a concrete page use the two below.
 */
export const APP_LANDING_PATH = APP_BASE
export const PREMIUM_LANDING_PATH = `${APP_BASE}/loyalty/pulse`
export const BASIC_LANDING_PATH = `${APP_BASE}/discovery/profile`
